import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mountain, Loader2 } from "lucide-react";

export default function AdminLogin() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      await login(email, password);
      toast.success("Welcome back!");
      nav("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="relative hidden lg:block">
        <img src="https://images.unsplash.com/photo-1718429205172-0d91b73ab23c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-secondary/70" />
        <div className="relative z-10 h-full flex flex-col justify-end p-12 text-white">
          <Mountain className="h-10 w-10 text-primary mb-4" />
          <h1 className="font-heading text-5xl font-semibold">Safarshala Admin</h1>
          <p className="text-white/70 mt-2 max-w-sm">Manage trips, departures, leads, bookings and messaging — all in one place.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 bg-background">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5">
          <div className="lg:hidden flex items-center gap-2 font-heading text-3xl mb-4"><Mountain className="h-7 w-7 text-primary" /> Safarshala</div>
          <div>
            <h2 className="font-heading text-4xl font-semibold">Sign in</h2>
            <p className="text-muted-foreground text-sm mt-1">Enter your admin credentials to continue.</p>
          </div>
          {error && <div className="rounded-lg bg-destructive/10 text-destructive text-sm p-3" data-testid="login-error">{error}</div>}
          <div>
            <Label>Email or Employee Code</Label>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" placeholder="you@email.com or EMP001" autoCapitalize="none" />
          </div>
          <div>
            <Label>Password</Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" placeholder="••••••••" />
          </div>
          <Button type="submit" disabled={loading} className="w-full rounded-full h-11" data-testid="login-submit">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign In"}
          </Button>
          <div className="flex justify-between text-sm">
            <Link to="/admin/forgot-password" className="text-primary hover:underline">Forgot password?</Link>
            <Link to="/" className="text-muted-foreground hover:text-primary">← Back to site</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
